import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useProgramReportCard, useProgramNavigation } from '../../../hooks/useProgramReportCard';
import { useUpdateProgramContacts } from '../../../hooks/useProgramContacts';
import { useUpdateProgramMedia } from '../../../hooks/useProgramMedia';
import { useUpdateProgramAiInstructions, useUpdateProgramStep0Flags, useUpdateProgramRequirementsQc, useUpdateProgramDeadlinesQc, useUpdateProgramJudgingQc, useUpdateProgramDetailsQc, useUpdateProgramVettingQc, useUpdateProgramDetailsData } from '../../../hooks/useProgramDetails';
import { useUpdateProgramFeesPrizes } from '../../../hooks/useProgramFeesPrizes';
import { useProgramLock } from '../../../hooks/useProgramLock';
import { useUrlValidation } from '../../../hooks/useUrlValidation';
import type { WorkflowStep } from '../shared/WorkflowProgress';
import type { URLValidatorRecommendation } from '../../../types/url-validation';
import { UrlDiscoveryStep } from '../components/UrlDiscoveryStep';
import { NewProgramPreparationStep } from '../components/NewProgramPreparationStep';
import {
  PreparationSection,
  ContactsSection,
  DeadlinesSection,
  RequirementsSection,
  FeesPrizesSection,
  CategoriesSection,
  JudgingSection,
  ProgramDetailsSection,
  VettingSection,
  type ContactsData,
  type DeadlinesData,
  type RequirementsData,
  type FeesPrizesData,
  type MediaData,
  type CategoriesData,
  type JudgingData,
  type ProgramDetailsData,
  type VettingData
} from './sections';
import { TableSection } from './TableSection';
import { QCHeader } from './QCHeader';
import { QCStepRail } from './QCStepRail';
import type { QCStepRailStep } from './QCStepRail';
import { QCStepHeader } from './QCStepHeader';
import { QCStepFooter } from './QCStepFooter';
import { QCNotesDrawer } from './QCNotesDrawer';
import { FieldRow } from '../shared';
import { vettingService } from '../../../services/vetting-service';
import type { VettingStatus as BackendVettingStatus } from '../../../types/vetting';
import styles from './QualityCheckCard.module.css';
import { getProgramReportCard, type ProgramReportCardData } from '../../../services/rpc';
import { getOverrideNavigationTargets, type OverrideNavTarget } from './categoryPriorityOrder';

interface TimerState {
  running: boolean;
  startedAt: number | null;
  accumulatedMs: number;
}

interface PersistedWorkflowState {
  timer: TimerState;
  checklist: Record<string, boolean>;
  notes: Record<string, string>;
}

type VettingUiStatus = 'approved' | 'review' | 'unsuitable';

const QC_STEP_DEFINITIONS = [
  { id: 'step0', label: 'Step 0: Preparation' },
  { id: 'step1', label: 'Step 1: Validate Seed URLs' },
  { id: 'step2', label: 'Step 2: Requirements' },
  { id: 'step3', label: 'Step 3: Deadlines' },
  { id: 'step4', label: 'Step 4: Fees & Prizes' },
  { id: 'step5', label: 'Step 5: Categories' },
  { id: 'step6', label: 'Step 6: Contacts' },
  { id: 'step7', label: 'Step 7: Judging' },
  { id: 'step8', label: 'Step 8: Program Details' },
  { id: 'step9', label: 'Step 9: Vetting' },
  { id: 'step10', label: 'Step 10: QA Report' },
  { id: 'step11', label: 'Step 11: Finalize Session' }
];

const LOCAL_STORAGE_PREFIX = 'qc-workflow-state';
const NEW_PROGRAM_LABEL = 'New Program Check';

const newProgramMetaStorageKey = (programId: string) => `${LOCAL_STORAGE_PREFIX}:${programId}:new-program-meta`;

const createEmptyReportCard = (programId: string, label: string): ProgramReportCardData => ({
  program_id: programId,
  program_name: label,
  baci_program_code: label,
  awarding_organization: null,
  founding_year: null,
  ai_instructions: null,
  qc_flag_preparation: null,
  qc_flag_preparation_notes: null,
  contact_name: null,
  contact_email: null,
  contact_phone: null,
  physical_address: null,
  contact_notes: null,
  qc_flag_contacts: null,
  qc_flag_contacts_notes: null,
  early_bird_deadline: null,
  regular_deadline: null,
  final_deadline: null,
  winners_announcement_date: null,
  announcement_timeline: null,
  award_ceremony_date: null,
  submission_period: null,
  deadline_structure: null,
  qc_flag_deadlines: null,
  qc_flag_deadlines_notes: null,
  self_published_accepted: null,
  isbn_required: null,
  geographic_restrictions: null,
  requirements_summary: null,
  accepted_formats: null,
  manuscript_requirements: null,
  deadline_type: null,
  publication_date_requirements: null,
  publication_years_accepted: null,
  qc_flag_requirements: null,
  qc_flag_requirements_notes: null,
  entry_fee: null,
  entry_fee_currency: null,
  cost_range: null,
  prize_structure: null,
  prizes_per_category: null,
  prize_types: null,
  total_win_opportunities: null,
  has_tiered_prizes: null,
  primary_currency: null,
  extra_benefits: null,
  qc_flag_fees_prizes: null,
  qc_flag_fees_prizes_notes: null,
  website_url: null,
  submission_url: null,
  past_winners_url: null,
  logo_url: null,
  website_screenshot_url: null,
  seed_urls: null,
  wikipedia_entry: null,
  categories_url: null,
  deadlines_url: null,
  fees_url: null,
  requirements_url: null,
  contact_url: null,
  guidelines_url: null,
  qc_flag_media: null,
  qc_flag_media_notes: null,
  number_of_categories: null,
  judging_criteria: null,
  celebration_type: null,
  qc_flag_judging: null,
  qc_flag_judging_notes: null,
  host_country: null,
  language_requirements: null,
  submission_type: null,
  has_audiobook_categories: null,
  bisac_default_primary_code: null,
  bisac_default_secondary_code: null,
  bisac_default_tertiary_code: null,
  bisac_preferred_edition_year: null,
  industry_warnings: null,
  qc_flag_program_details: null,
  qc_flag_program_details_notes: null,
  categories: [],
  qc_flag_categories: null,
  qc_flag_categories_notes: null,
  program_vetting_status: null,
  vetting_notes: null,
  qc_flag_vetting: null,
  qc_flag_vetting_notes: null
});

const formatDuration = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const createDefaultWorkflowState = (): PersistedWorkflowState => ({
  timer: {
    running: false,
    startedAt: null,
    accumulatedMs: 0
  },
  checklist: Object.fromEntries(QC_STEP_DEFINITIONS.map((step) => [step.id, false])),
  notes: Object.fromEntries(QC_STEP_DEFINITIONS.map((step) => [step.id, '']))
});

const mapBackendVettingToUi = (status?: BackendVettingStatus | null): VettingUiStatus => {
  switch (status) {
    case 'green':
      return 'approved';
    case 'red':
      return 'unsuitable';
    case 'yellow':
    default:
      return 'review';
  }
};

const mapUiVettingToBackend = (status: VettingUiStatus): BackendVettingStatus => {
  switch (status) {
    case 'approved':
      return 'green';
    case 'unsuitable':
      return 'red';
    case 'review':
    default:
      return 'yellow';
  }
};

const loadPersistedState = (programId: string): PersistedWorkflowState => {
  const defaults = createDefaultWorkflowState();
  try {
    const raw = localStorage.getItem(`${LOCAL_STORAGE_PREFIX}:${programId}`);
    if (!raw) {
      return defaults;
    }

    const parsed = JSON.parse(raw) as Partial<PersistedWorkflowState>;
    const storedTimer = parsed?.timer ?? defaults.timer;
    const running = Boolean(storedTimer.running);
    const startedAt = typeof storedTimer.startedAt === 'number' ? storedTimer.startedAt : null;
    const accumulatedMs = typeof storedTimer.accumulatedMs === 'number' ? storedTimer.accumulatedMs : 0;

    let normalizedTimer: TimerState = {
      running: false,
      startedAt: null,
      accumulatedMs
    };

    if (running && startedAt) {
      normalizedTimer = {
        running: false,
        startedAt: null,
        accumulatedMs: accumulatedMs + Math.max(0, Date.now() - startedAt)
      };
    }

    return {
      timer: normalizedTimer,
      checklist: { ...defaults.checklist, ...(parsed?.checklist ?? {}) },
      notes: { ...defaults.notes, ...(parsed?.notes ?? {}) }
    };
  } catch (error) {
    console.warn('[QC] Failed to load workflow state', error);
    return defaults;
  }
};

const getFirstIncompleteStepId = (checklist: Record<string, boolean>): string => {
  const next = QC_STEP_DEFINITIONS.find((step) => !checklist[step.id]);
  return next?.id ?? QC_STEP_DEFINITIONS[0].id;
};

interface HeaderRunConfig {
  label: string;
  run: () => Promise<void> | void;
  disabled?: boolean;
}

export interface QualityCheckCardProps {
  programId: string;
  baciCode: string;
  className?: string;
  mode?: 'existing' | 'new-program';
}

export function QualityCheckCard({ programId, baciCode, className = '', mode = 'existing' }: QualityCheckCardProps) {
  const queryClient = useQueryClient();
  const isNewProgram = mode === 'new-program';

  const {
    data: reportData,
    isLoading: reportLoading,
    error,
    refetch
  } = useProgramReportCard(baciCode, { enabled: !isNewProgram });
  const { data: navData } = useProgramNavigation(baciCode, { enabled: !isNewProgram });

  const data: ProgramReportCardData | null = isNewProgram
    ? createEmptyReportCard(programId, NEW_PROGRAM_LABEL)
    : reportData ?? null;
  const isLoading = isNewProgram ? false : reportLoading;
  const effectiveNavData = isNewProgram ? null : navData ?? null;
  const overrideTargets = useMemo(
    () => (isNewProgram ? null : getOverrideNavigationTargets(baciCode)),
    [isNewProgram, baciCode]
  );
  const overridePrevious = overrideTargets?.previous;
  const overrideNext = overrideTargets?.next;

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [contactsChanges, setContactsChanges] = useState<Partial<ContactsData>>({});
  const [mediaChanges, setMediaChanges] = useState<Partial<MediaData>>({});
  const [programMetaChanges, setProgramMetaChanges] = useState<{
    ai_instructions?: string | null;
    qc_flag_preparation?: boolean;
    qc_flag_preparation_notes?: string | null;
  }>({});
  const [requirementsChanges, setRequirementsChanges] = useState<Partial<RequirementsData>>({});
  const [deadlinesChanges, setDeadlinesChanges] = useState<Partial<DeadlinesData>>({});
  const [feesPrizesChanges, setFeesPrizesChanges] = useState<Partial<FeesPrizesData>>({});
  const [judgingChanges, setJudgingChanges] = useState<Partial<JudgingData>>({});
  const [programDetailsChanges, setProgramDetailsChanges] = useState<Partial<ProgramDetailsData>>({});
  const [vettingChanges, setVettingChanges] = useState<Partial<VettingData>>({});

  const [newProgramMeta, setNewProgramMeta] = useState<{ programUrl: string; validationQuestion: string }>(() => {
    if (!isNewProgram || typeof window === 'undefined') {
      return { programUrl: '', validationQuestion: '' };
    }

    try {
      const stored = localStorage.getItem(newProgramMetaStorageKey(programId));
      if (stored) {
        const parsed = JSON.parse(stored) as { programUrl?: string; validationQuestion?: string };
        return {
          programUrl: parsed.programUrl ?? '',
          validationQuestion: parsed.validationQuestion ?? ''
        };
      }
    } catch (metaError) {
      console.warn('[QC] Failed to load new program metadata', metaError);
    }

    return { programUrl: '', validationQuestion: '' };
  });

  const persistedStateRef = useRef<PersistedWorkflowState>(loadPersistedState(programId));
  const [timerState, setTimerState] = useState<TimerState>(() => persistedStateRef.current.timer);
  const [checklistState, setChecklistState] = useState<Record<string, boolean>>(
    () => persistedStateRef.current.checklist
  );
  const [notesState, setNotesState] = useState<Record<string, string>>(
    () => persistedStateRef.current.notes
  );
  const [activeStepId, setActiveStepId] = useState<string>(() => getFirstIncompleteStepId(persistedStateRef.current.checklist));
  const [notesOpen, setNotesOpen] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [, setTimerTick] = useState(0);
  const [registeredRunConfig, setRegisteredRunConfig] = useState<HeaderRunConfig | null>(null);
  const [vettingStatus, setVettingStatus] = useState<VettingUiStatus>('review');
  const [vettingSaving, setVettingSaving] = useState(false);
  const [navigationLoading, setNavigationLoading] = useState<'previous' | 'next' | null>(null);
  const handleRegisterRun = useCallback((config: HeaderRunConfig | null) => {
    setRegisteredRunConfig(config);
  }, []);

  const urlDiscoveryRunnerRef = useRef<(() => Promise<void>) | null>(null);
  const [hasDiscoveryRunner, setHasDiscoveryRunner] = useState(false);
  const [isDiscoveryRunning, setIsDiscoveryRunning] = useState(false);
  const handleRegisterDiscoveryRunner = useCallback((runner: (() => Promise<void>) | null) => {
    urlDiscoveryRunnerRef.current = runner;
    setHasDiscoveryRunner(Boolean(runner));
  }, []);
  const handleDiscoveryLoadingChange = useCallback((loading: boolean) => {
    setIsDiscoveryRunning(loading);
  }, []);

  const messageTimerRef = useRef<number | null>(null);
  const previousProgramIdRef = useRef(programId);
  const autoSaveTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (messageTimerRef.current !== null) {
        clearTimeout(messageTimerRef.current);
      }
      if (autoSaveTimerRef.current !== null) {
        window.clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  const showMessage = useCallback((type: 'success' | 'error', text: string, duration: number) => {
    if (messageTimerRef.current !== null) {
      clearTimeout(messageTimerRef.current);
    }

    setSaveMessage({ type, text });
    messageTimerRef.current = window.setTimeout(() => {
      setSaveMessage(null);
      messageTimerRef.current = null;
    }, duration);
  }, []);

  const resolveProgramIdForBaci = useCallback(async (targetBaciCode: string): Promise<string | null> => {
    const normalized = targetBaciCode?.trim();
    if (!normalized) {
      return null;
    }

    const queryKey = ['program-report-card', normalized];
    const cached = queryClient.getQueryData<ProgramReportCardData>(queryKey);
    if (cached?.program_id) {
      return cached.program_id;
    }

    try {
      const result = await queryClient.fetchQuery({
        queryKey,
        queryFn: () => getProgramReportCard(normalized)
      });
      return result.program_id ?? null;
    } catch (fetchError) {
      console.error(`[QC] Failed to resolve program id for BACI ${normalized}`, fetchError);
      return null;
    }
  }, [queryClient]);

  const navigateUsingOverride = useCallback(async (
    target: OverrideNavTarget | undefined,
    direction: 'previous' | 'next'
  ): Promise<boolean> => {
    if (!target) {
      return false;
    }

    let programId = target.programId ?? null;

    if (!programId) {
      setNavigationLoading(direction);
      try {
        programId = await resolveProgramIdForBaci(target.baciCode);
      } finally {
        setNavigationLoading(null);
      }
    }

    if (!programId) {
      showMessage('error', 'Unable to load descending order program. Falling back to alphabetical order.', 4000);
      return false;
    }

    window.location.hash = `#quality-checkv2?program_id=${encodeURIComponent(programId)}&program_code=${encodeURIComponent(target.baciCode)}`;
    return true;
  }, [resolveProgramIdForBaci, showMessage]);

  const navigateWithFallback = useCallback((target: { id: string; baci_program_code: string } | null | undefined) => {
    if (!target) {
      return;
    }
    window.location.hash = `#quality-checkv2?program_id=${encodeURIComponent(target.id)}&program_code=${encodeURIComponent(target.baci_program_code)}`;
  }, []);

  const triggerPreviousNavigation = useCallback(async () => {
    if (isNewProgram) {
      return;
    }

    if (await navigateUsingOverride(overridePrevious, 'previous')) {
      return;
    }

    navigateWithFallback(effectiveNavData?.previousProgram ?? null);
  }, [isNewProgram, navigateUsingOverride, overridePrevious, effectiveNavData, navigateWithFallback]);

  const triggerNextNavigation = useCallback(async () => {
    if (isNewProgram) {
      return;
    }

    if (await navigateUsingOverride(overrideNext, 'next')) {
      return;
    }

    navigateWithFallback(effectiveNavData?.nextProgram ?? null);
  }, [isNewProgram, navigateUsingOverride, overrideNext, effectiveNavData, navigateWithFallback]);

  const handlePreviousProgramClick = useCallback(() => {
    void triggerPreviousNavigation();
  }, [triggerPreviousNavigation]);

  const handleNextProgramClick = useCallback(() => {
    void triggerNextNavigation();
  }, [triggerNextNavigation]);

  useEffect(() => {
    if (previousProgramIdRef.current === programId) {
      return;
    }

    previousProgramIdRef.current = programId;
    const restored = loadPersistedState(programId);
    persistedStateRef.current = restored;
    setTimerState(restored.timer);
    setChecklistState(restored.checklist);
    setNotesState(restored.notes);
    setActiveStepId(getFirstIncompleteStepId(restored.checklist));
  }, [programId]);

  useEffect(() => {
    try {
      const payload: PersistedWorkflowState = {
        timer: timerState,
        checklist: checklistState,
        notes: notesState
      };
      localStorage.setItem(`${LOCAL_STORAGE_PREFIX}:${programId}`, JSON.stringify(payload));
    } catch (persistError) {
      console.warn('[QC] Failed to persist workflow state', persistError);
    }
  }, [programId, timerState, checklistState, notesState]);

useEffect(() => {
  if (!isNewProgram) {
    return;
  }

    try {
      localStorage.setItem(newProgramMetaStorageKey(programId), JSON.stringify(newProgramMeta));
    } catch (persistMetaError) {
      console.warn('[QC] Failed to persist new program metadata', persistMetaError);
    }
}, [isNewProgram, programId, newProgramMeta]);

useEffect(() => {
  let cancelled = false;

  if (isNewProgram) {
    setVettingStatus('review');
    setVettingSaving(false);
    return () => {
      cancelled = true;
    };
  }

  setVettingSaving(true);
  void vettingService.getVetting(programId)
    .then((response) => {
      if (cancelled) {
        return;
      }
      setVettingStatus(mapBackendVettingToUi(response.status));
    })
    .catch((error) => {
      if (cancelled) {
        return;
      }
      const message = error instanceof Error ? error.message : 'Failed to load vetting status';
      showMessage('error', message, 4000);
      setVettingStatus('review');
    })
    .finally(() => {
      if (!cancelled) {
        setVettingSaving(false);
      }
    });

  return () => {
    cancelled = true;
  };
}, [isNewProgram, programId, showMessage]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setProgramDetailsChanges({});
    setVettingChanges({});
  }, [data]);

  useEffect(() => {
    if (!isNewProgram) {
      return;
    }

    const hasRequiredFields = newProgramMeta.programUrl.trim() !== '' && newProgramMeta.validationQuestion.trim() !== '';
    if (!hasRequiredFields && checklistState.step0) {
      setChecklistState((prev) => ({
        ...prev,
        step0: false
      }));
    }
  }, [isNewProgram, newProgramMeta, checklistState.step0]);

  useEffect(() => {
    if (!timerState.running || timerState.startedAt === null) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setTimerTick((tick) => tick + 1);
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [timerState.running, timerState.startedAt]);

  const effectiveElapsedMs = timerState.running && timerState.startedAt !== null
    ? timerState.accumulatedMs + Math.max(0, Date.now() - timerState.startedAt)
    : timerState.accumulatedMs;

  const timerLabel = formatDuration(effectiveElapsedMs);

  const handleToggleTimer = () => {
    setTimerState((prev) => {
      if (prev.running) {
        if (prev.startedAt === null) {
          return { running: false, startedAt: null, accumulatedMs: prev.accumulatedMs };
        }
        const elapsed = prev.accumulatedMs + Math.max(0, Date.now() - prev.startedAt);
        return { running: false, startedAt: null, accumulatedMs: elapsed };
      }
      return { running: true, startedAt: Date.now(), accumulatedMs: prev.accumulatedMs };
    });
    setTimerTick((tick) => tick + 1);
  };

  const handleResetTimer = () => {
    setTimerState({ running: false, startedAt: null, accumulatedMs: 0 });
    setTimerTick((tick) => tick + 1);
  };

  const {
    validation,
    runValidation,
    validating,
    urlsDirty,
    markUrlsClean
  } = useUrlValidation(isNewProgram ? null : programId);

  const handleChecklistToggle = useCallback((stepId: string, completed: boolean) => {
    setChecklistState((prev) => {
      const next = {
        ...prev,
        [stepId]: completed
      };

      if (!completed) {
        setActiveStepId(stepId);
      } else {
        const nextIncomplete = QC_STEP_DEFINITIONS.find((step) => !next[step.id]);
        if (nextIncomplete) {
          setActiveStepId(nextIncomplete.id);
        }
      }

      return next;
    });
  }, []);

const handleRunValidation = useCallback(async () => {
  if (isNewProgram) {
    showMessage('error', 'URL validation is only available after the program has been added to the database.', 5000);
    return;
  }

    try {
      await runValidation({});
      markUrlsClean();
      handleChecklistToggle('step1', true);
      showMessage('success', 'URL validation completed', 2500);
    } catch (runError) {
      console.error('[QC] URL validation failed', runError);
      showMessage('error', runError instanceof Error ? runError.message : 'Validation failed', 4000);
  }
}, [isNewProgram, runValidation, markUrlsClean, handleChecklistToggle, showMessage]);

const handleVettingStatusChange = useCallback(async (next: VettingUiStatus): Promise<boolean> => {
  setVettingStatus(next);

  if (isNewProgram) {
    return true;
  }

  setVettingSaving(true);
  let succeeded = false;
  try {
    const backendStatus = mapUiVettingToBackend(next);
    const response = await vettingService.setVettingStatus(programId, backendStatus, 'qc_workflow_update');
    if (!response.success) {
      throw new Error(response.error ?? 'Unknown error');
    }
    showMessage('success', 'Vetting status updated', 2500);
    succeeded = true;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to update vetting status';
    showMessage('error', message, 4000);
    // revert to previous value from server on failure
    try {
      const latest = await vettingService.getVetting(programId);
      setVettingStatus(mapBackendVettingToUi(latest.status));
    } catch (refreshError) {
      console.error('Failed to refresh vetting status after error:', refreshError);
      setVettingStatus('review');
    }
  } finally {
    setVettingSaving(false);
  }

  return succeeded;
}, [isNewProgram, programId, showMessage]);

  const handleNoteChange = (stepId: string, value: string) => {
    if (stepId === 'step0') {
      setProgramMetaChanges((prev) => ({
        ...prev,
        qc_flag_preparation_notes: value
      }));
      setNotesState((prev) => ({
        ...prev,
        step0: value
      }));
      markDirty();
      return;
    }

    if (stepId === 'step0-ai') {
      setProgramMetaChanges((prev) => ({
        ...prev,
        ai_instructions: value
      }));
      markDirty();
      return;
    }

    if (stepId === 'step1') {
      setMediaChanges((prev) => ({
        ...prev,
        qc_flag_media_notes: value
      }));
      markDirty();
      return;
    }

    if (stepId === 'step2') {
      setRequirementsChanges((prev) => ({
        ...prev,
        qc_flag_requirements_notes: value
      }));
      markDirty();
      return;
    }

    if (stepId === 'step3') {
      setDeadlinesChanges((prev) => ({
        ...prev,
        qc_flag_deadlines_notes: value
      }));
      markDirty();
      return;
    }

    if (stepId === 'step4') {
      setFeesPrizesChanges((prev) => ({
        ...prev,
        qc_flag_fees_prizes_notes: value
      }));
      markDirty();
      return;
    }

    if (stepId === 'step6') {
      setContactsChanges((prev) => ({
        ...prev,
        qc_flag_contacts_notes: value
      }));
      markDirty();
      return;
    }

    if (stepId === 'step7') {
      setJudgingChanges((prev) => ({
        ...prev,
        qc_flag_judging_notes: value
      }));
      markDirty();
      return;
    }

    if (stepId === 'step8') {
      setProgramDetailsChanges((prev) => ({
        ...prev,
        qc_flag_program_details_notes: value
      }));
      markDirty();
      return;
    }

    if (stepId === 'step9') {
      setVettingChanges((prev) => ({
        ...prev,
        qc_flag_vetting_notes: value
      }));
      markDirty();
      return;
    }

    setNotesState((prev) => ({
      ...prev,
      [stepId]: value
    }));
    markDirty();
  };

  const updateContactsMutation = useUpdateProgramContacts({
    onSuccess: () => {
      showMessage('success', 'Contacts saved successfully!', 3000);
      setHasUnsavedChanges(false);
      setContactsChanges({});
      if (!isNewProgram) {
        void refetch();
      }
    },
    onError: (mutError) => {
      showMessage('error', `Save failed: ${mutError.message}`, 5000);
    }
  });

  const updateMediaMutation = useUpdateProgramMedia({
    onSuccess: () => {
      showMessage('success', 'URL fields saved successfully!', 3000);
      setHasUnsavedChanges(false);
      setMediaChanges({});
      if (!isNewProgram) {
        void refetch();
      }
    },
    onError: (mutError) => {
      showMessage('error', `Save failed: ${mutError.message}`, 5000);
    }
  });

  const updateAiInstructionsMutation = useUpdateProgramAiInstructions({
    onSuccess: () => {
      showMessage('success', 'AI instructions saved successfully!', 3000);
      setHasUnsavedChanges(false);
      setProgramMetaChanges({});
      if (!isNewProgram) {
        void refetch();
      }
    },
    onError: (mutError) => {
      showMessage('error', `Save failed: ${mutError.message}`, 5000);
    }
  });

  const updateStep0FlagsMutation = useUpdateProgramStep0Flags({
    onSuccess: () => {
      showMessage('success', 'Step 0 flags saved successfully!', 3000);
      setHasUnsavedChanges(false);
      if (!isNewProgram) {
        void refetch();
      }
    },
    onError: (mutError) => {
      showMessage('error', `Save failed: ${mutError.message}`, 5000);
    }
  });

  const updateFeesPrizesMutation = useUpdateProgramFeesPrizes({
    onSuccess: () => {
      showMessage('success', 'Fees & prizes saved successfully!', 3000);
      setHasUnsavedChanges(false);
      setFeesPrizesChanges({});
      if (!isNewProgram) {
        void refetch();
      }
    },
    onError: (mutError) => {
      showMessage('error', `Save failed: ${mutError.message}`, 5000);
    }
  });

  const updateRequirementsQcMutation = useUpdateProgramRequirementsQc({
    onSuccess: () => {
      showMessage('success', 'Requirements QC notes saved successfully!', 3000);
      setHasUnsavedChanges(false);
      setRequirementsChanges({});
      if (!isNewProgram) {
        void refetch();
      }
    },
    onError: (mutError) => {
      showMessage('error', `Save failed: ${mutError.message}`, 5000);
    }
  });

  const updateDeadlinesQcMutation = useUpdateProgramDeadlinesQc({
    onSuccess: () => {
      showMessage('success', 'Deadlines QC notes saved successfully!', 3000);
      setHasUnsavedChanges(false);
      setDeadlinesChanges({});
      if (!isNewProgram) {
        void refetch();
      }
    },
    onError: (mutError) => {
      showMessage('error', `Save failed: ${mutError.message}`, 5000);
    }
  });

  const updateJudgingQcMutation = useUpdateProgramJudgingQc({
    onSuccess: () => {
      showMessage('success', 'Judging QC notes saved successfully!', 3000);
      setHasUnsavedChanges(false);
      setJudgingChanges({});
      if (!isNewProgram) {
        void refetch();
      }
    },
    onError: (mutError) => {
      showMessage('error', `Save failed: ${mutError.message}`, 5000);
    }
  });

  const updateProgramDetailsQcMutation = useUpdateProgramDetailsQc({
    onSuccess: () => {
      showMessage('success', 'Program details QC saved successfully!', 3000);
      setHasUnsavedChanges(false);
      setProgramDetailsChanges({});
      if (!isNewProgram) {
        void refetch();
      }
    },
    onError: (mutError) => {
      showMessage('error', `Save failed: ${mutError.message}`, 5000);
    }
  });

  const updateProgramVettingQcMutation = useUpdateProgramVettingQc({
    onSuccess: () => {
      showMessage('success', 'Vetting QC saved successfully!', 3000);
      setHasUnsavedChanges(false);
      setVettingChanges({});
      if (!isNewProgram) {
        void refetch();
      }
    },
    onError: (mutError) => {
      showMessage('error', `Save failed: ${mutError.message}`, 5000);
    }
  });

  const updateProgramDetailsDataMutation = useUpdateProgramDetailsData({
    onSuccess: () => {
      showMessage('success', 'Program details saved successfully!', 3000);
      setProgramDetailsChanges({});
      if (!isNewProgram) {
        void refetch();
      }
    },
    onError: (mutError) => {
      showMessage('error', `Save failed: ${mutError.message}`, 5000);
    }
  });

  const {
    isLocked,
    isLoading: lockStatusLoading,
    locking,
    unlocking,
    lockProgram,
    unlockProgram,
    refetchLock
  } = useProgramLock(isNewProgram ? null : programId);

  const isLockMutationPending = locking || unlocking;
  const [optimisticLockState, setOptimisticLockState] = useState<boolean | null>(null);
  const effectiveLockState = optimisticLockState ?? isLocked;
  const pendingLockAction: 'lock' | 'unlock' | null = optimisticLockState === null
    ? null
    : (optimisticLockState ? 'lock' : 'unlock');

  const handleToggleLock = async () => {
    if (effectiveLockState) {
      setOptimisticLockState(false);
      try {
        await unlockProgram({
          reason: 'User unlocked from Quality Check card',
          metadata: {
            source: 'quality-check-card',
            action: 'unlock_for_editing'
          }
        });
        await refetchLock();
        showMessage('success', '🔓 Data unlocked - editing enabled', 2500);
      } catch (lockError) {
        setOptimisticLockState(null);
        const message = lockError instanceof Error ? lockError.message : 'Unknown error';
        showMessage('error', `Failed to unlock: ${message}`, 5000);
      }
      return;
    }

    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Locking will discard them.\n\nContinue?'
      );
      if (!confirmed) {
        return;
      }
    }

    try {
      setOptimisticLockState(true);
      await lockProgram({
        reason: 'User locked from Quality Check card',
        metadata: {
          source: 'quality-check-card',
          action: 'lock_to_view_mode'
        }
      });
      await refetchLock();
      setHasUnsavedChanges(false);
      setContactsChanges({});
      setMediaChanges({});
      setRequirementsChanges({});
      setDeadlinesChanges({});
      setFeesPrizesChanges({});
      setJudgingChanges({});
      showMessage('success', '🔒 Data locked - view mode enabled', 2500);
    } catch (lockError) {
      setOptimisticLockState(null);
      const message = lockError instanceof Error ? lockError.message : 'Unknown error';
      showMessage('error', `Failed to lock: ${message}`, 5000);
    }
  };

  useEffect(() => {
    if (optimisticLockState === null) return;
    if (isLocked === optimisticLockState) {
      setOptimisticLockState(null);
    }
  }, [isLocked, optimisticLockState]);

  useEffect(() => {
  const hasDraft = programMetaChanges.ai_instructions !== undefined;
    if (!data || hasDraft) {
      return;
    }

    const baseStep0Notes = data.qc_flag_preparation_notes ?? '';
    setNotesState((prev) => {
      if (prev.step0 === baseStep0Notes) {
        return prev;
      }
      return {
        ...prev,
        step0: baseStep0Notes
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.qc_flag_preparation_notes]);

  const editMode = !lockStatusLoading && !effectiveLockState;
  const displayLocked = effectiveLockState;

  const scheduleAutoSave = () => {
    if (isNewProgram) {
      return;
    }

    if (!editMode) {
      return;
    }

    if (autoSaveTimerRef.current !== null) {
      window.clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = window.setTimeout(() => {
      void persistPendingChanges(false);
    }, 1200);
  };

  const markDirty = () => {
    setHasUnsavedChanges(true);
    if (isNewProgram) {
      return;
    }
    scheduleAutoSave();
  };

  if (isLoading) {
    return (
      <div className={`${styles.container} ${className}`}>
        <div className={styles.placeholderCard}>
          <h2>Loading quality check data…</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${styles.container} ${className}`}>
        <div className={styles.placeholderCard}>
          <h2>Error Loading Data</h2>
          <p>{error.message}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`${styles.container} ${className}`}>
        <div className={styles.placeholderCard}>
          <h2>Program Not Found</h2>
          <p>No data available for program ID: {programId}</p>
        </div>
      </div>
    );
  }

  const baseAiInstructions = data.ai_instructions ?? '';
  const draftAiInstructions = programMetaChanges.ai_instructions;
  const effectiveAiInstructions = draftAiInstructions !== undefined
    ? (draftAiInstructions ?? '')
    : baseAiInstructions;

  const displayProgramName = isNewProgram ? NEW_PROGRAM_LABEL : data.program_name;
  const displayProgramCode = isNewProgram ? NEW_PROGRAM_LABEL : baciCode;
  const displayWebsiteUrl = isNewProgram
    ? (newProgramMeta.programUrl.trim() ? newProgramMeta.programUrl.trim() : undefined)
    : data.website_url || undefined;
  const fallbackHasPrevious = !isNewProgram && Boolean(effectiveNavData?.hasPrevious);
  const fallbackHasNext = !isNewProgram && Boolean(effectiveNavData?.hasNext);
  const previousAvailable = !isNewProgram && (Boolean(overridePrevious) || fallbackHasPrevious);
  const nextAvailable = !isNewProgram && (Boolean(overrideNext) || fallbackHasNext);
  const previousButtonEnabled = previousAvailable && navigationLoading !== 'previous';
  const nextButtonEnabled = nextAvailable && navigationLoading !== 'next';

  const contactsData: ContactsData = {
    contact_name: data.contact_name,
    contact_email: data.contact_email,
    contact_phone: data.contact_phone,
    physical_address: data.physical_address,
    contact_notes: data.contact_notes,
    qc_flag_contacts: data.qc_flag_contacts,
    qc_flag_contacts_notes: data.qc_flag_contacts_notes
  };

  const deadlinesData: DeadlinesData = {
    early_bird_deadline: data.early_bird_deadline,
    regular_deadline: data.regular_deadline,
    final_deadline: data.final_deadline,
    winners_announcement_date: data.winners_announcement_date,
    announcement_timeline: data.announcement_timeline,
    award_ceremony_date: data.award_ceremony_date,
    submission_period: data.submission_period,
    deadline_structure: data.deadline_structure,
    qc_flag_deadlines: data.qc_flag_deadlines,
    qc_flag_deadlines_notes: data.qc_flag_deadlines_notes
  };

  const requirementsData: RequirementsData = {
    self_published_accepted: data.self_published_accepted,
    isbn_required: data.isbn_required,
    geographic_restrictions: data.geographic_restrictions,
    publication_date_requirements: data.publication_date_requirements,
    accepted_formats: data.accepted_formats,
    manuscript_requirements: data.manuscript_requirements,
    deadline_type: data.deadline_type,
    requirements_summary: data.requirements_summary,
    publication_years_accepted: data.publication_years_accepted,
    qc_flag_requirements: data.qc_flag_requirements,
    qc_flag_requirements_notes: data.qc_flag_requirements_notes
  };

  const feesPrizesData: FeesPrizesData = {
    entry_fee: data.entry_fee,
    entry_fee_currency: data.entry_fee_currency,
    cost_range: data.cost_range,
    prize_structure: data.prize_structure,
    prizes_per_category: data.prizes_per_category,
    prize_types: data.prize_types,
    total_win_opportunities: data.total_win_opportunities,
    has_tiered_prizes: data.has_tiered_prizes,
    primary_currency: data.primary_currency,
    extra_benefits: data.extra_benefits,
    qc_flag_fees_prizes: data.qc_flag_fees_prizes,
    qc_flag_fees_prizes_notes: data.qc_flag_fees_prizes_notes
  };

  const mediaData: MediaData = {
    website_url: data.website_url,
    submission_url: data.submission_url,
    past_winners_url: data.past_winners_url,
    logo_url: data.logo_url || null,
    website_screenshot_url: data.website_screenshot_url || null,
    seed_urls: data.seed_urls,
    wikipedia_entry: data.wikipedia_entry,
    categories_url: data.categories_url || null,
    deadlines_url: data.deadlines_url || null,
    fees_url: data.fees_url || null,
    requirements_url: data.requirements_url || null,
    contact_url: data.contact_url || null,
    guidelines_url: data.guidelines_url || null,
    qc_flag_media: data.qc_flag_media,
    qc_flag_media_notes: data.qc_flag_media_notes
  };

  const categoriesData: CategoriesData = {
    categories: data.categories || [],
    qc_flag_categories: data.qc_flag_categories,
    qc_flag_categories_notes: data.qc_flag_categories_notes
  };

  const judgingData: JudgingData = {
    number_of_categories: data.number_of_categories,
    judging_criteria: data.judging_criteria,
    celebration_type: data.celebration_type,
    qc_flag_judging: data.qc_flag_judging,
    qc_flag_judging_notes: data.qc_flag_judging_notes
  };

  const programDetailsData: ProgramDetailsData = {
    host_country: data.host_country,
    language_requirements: data.language_requirements,
    submission_type: data.submission_type,
    has_audiobook_categories: data.has_audiobook_categories,
    bisac_default_primary_code: data.bisac_default_primary_code,
    bisac_default_secondary_code: data.bisac_default_secondary_code,
    bisac_default_tertiary_code: data.bisac_default_tertiary_code,
    bisac_preferred_edition_year: data.bisac_preferred_edition_year,
    industry_warnings: data.industry_warnings,
    qc_flag_program_details: data.qc_flag_program_details,
    qc_flag_program_details_notes: data.qc_flag_program_details_notes
  };

  const vettingData: VettingData = {
    program_vetting_status: data.program_vetting_status,
    vetting_notes: data.vetting_notes,
    qc_flag_vetting: data.qc_flag_vetting,
    qc_flag_vetting_notes: data.qc_flag_vetting_notes
  };

  const persistPendingChanges = async (notifySuccess = true) => {
    if (isNewProgram) {
      return;
    }

    if (
      updateContactsMutation.isPending
      || updateMediaMutation.isPending
      || updateAiInstructionsMutation.isPending
      || updateStep0FlagsMutation.isPending
      || updateFeesPrizesMutation.isPending
      || updateRequirementsQcMutation.isPending
      || updateDeadlinesQcMutation.isPending
      || updateJudgingQcMutation.isPending
      || updateProgramDetailsQcMutation.isPending
      || updateProgramVettingQcMutation.isPending
      || updateProgramDetailsDataMutation.isPending
    ) {
      return;
    }

    const hasMediaChanges = Object.keys(mediaChanges).length > 0;
    const hasContactChanges = Object.keys(contactsChanges).length > 0;
    const hasAiInstructionChanges = Object.prototype.hasOwnProperty.call(programMetaChanges, 'ai_instructions');
    const hasStep0FlagsChanges = Object.prototype.hasOwnProperty.call(programMetaChanges, 'qc_flag_preparation') || Object.prototype.hasOwnProperty.call(programMetaChanges, 'qc_flag_preparation_notes');
    const hasFeesPrizesChanges = Object.keys(feesPrizesChanges).length > 0;
    const hasRequirementsQcChanges = Object.prototype.hasOwnProperty.call(requirementsChanges, 'qc_flag_requirements') || Object.prototype.hasOwnProperty.call(requirementsChanges, 'qc_flag_requirements_notes');
    const hasDeadlinesQcChanges = Object.prototype.hasOwnProperty.call(deadlinesChanges, 'qc_flag_deadlines') || Object.prototype.hasOwnProperty.call(deadlinesChanges, 'qc_flag_deadlines_notes');
    const hasJudgingQcChanges = Object.prototype.hasOwnProperty.call(judgingChanges, 'qc_flag_judging') || Object.prototype.hasOwnProperty.call(judgingChanges, 'qc_flag_judging_notes');
    const hasProgramDetailsQcChanges = Object.prototype.hasOwnProperty.call(programDetailsChanges, 'qc_flag_program_details') || Object.prototype.hasOwnProperty.call(programDetailsChanges, 'qc_flag_program_details_notes');
    const hasVettingQcChanges = Object.prototype.hasOwnProperty.call(vettingChanges, 'qc_flag_vetting') || Object.prototype.hasOwnProperty.call(vettingChanges, 'qc_flag_vetting_notes');

    const normalizeString = (value: string | null | undefined) => {
      if (value == null) return null;
      const trimmed = value.trim();
      return trimmed === '' ? null : trimmed;
    };

    const mergedProgramDetails = {
      host_country: programDetailsChanges.host_country ?? programDetailsData.host_country ?? null,
      language_requirements: programDetailsChanges.language_requirements ?? programDetailsData.language_requirements ?? null,
      submission_type: programDetailsChanges.submission_type ?? programDetailsData.submission_type ?? null,
      has_audiobook_categories: programDetailsChanges.has_audiobook_categories ?? programDetailsData.has_audiobook_categories ?? null,
      bisac_default_primary_code: programDetailsChanges.bisac_default_primary_code ?? programDetailsData.bisac_default_primary_code ?? null,
      bisac_default_secondary_code: programDetailsChanges.bisac_default_secondary_code ?? programDetailsData.bisac_default_secondary_code ?? null,
      bisac_default_tertiary_code: programDetailsChanges.bisac_default_tertiary_code ?? programDetailsData.bisac_default_tertiary_code ?? null,
      bisac_preferred_edition_year: programDetailsChanges.bisac_preferred_edition_year ?? programDetailsData.bisac_preferred_edition_year ?? null,
      industry_warnings: programDetailsChanges.industry_warnings ?? programDetailsData.industry_warnings ?? null
    };

    const normalizedProgramDetailsNext = {
      host_country: normalizeString(mergedProgramDetails.host_country),
      language_requirements: normalizeString(mergedProgramDetails.language_requirements),
      submission_type: normalizeString(mergedProgramDetails.submission_type),
      has_audiobook_categories: mergedProgramDetails.has_audiobook_categories,
      bisac_default_primary_code: normalizeString(mergedProgramDetails.bisac_default_primary_code),
      bisac_default_secondary_code: normalizeString(mergedProgramDetails.bisac_default_secondary_code),
      bisac_default_tertiary_code: normalizeString(mergedProgramDetails.bisac_default_tertiary_code),
      bisac_preferred_edition_year: normalizeString(mergedProgramDetails.bisac_preferred_edition_year),
      industry_warnings: normalizeString(mergedProgramDetails.industry_warnings)
    };

    const normalizedProgramDetailsExisting = {
      host_country: normalizeString(programDetailsData.host_country ?? null),
      language_requirements: normalizeString(programDetailsData.language_requirements ?? null),
      submission_type: normalizeString(programDetailsData.submission_type ?? null),
      has_audiobook_categories: programDetailsData.has_audiobook_categories ?? null,
      bisac_default_primary_code: normalizeString(programDetailsData.bisac_default_primary_code ?? null),
      bisac_default_secondary_code: normalizeString(programDetailsData.bisac_default_secondary_code ?? null),
      bisac_default_tertiary_code: normalizeString(programDetailsData.bisac_default_tertiary_code ?? null),
      bisac_preferred_edition_year: normalizeString(programDetailsData.bisac_preferred_edition_year ?? null),
      industry_warnings: normalizeString(programDetailsData.industry_warnings ?? null)
    };

    const hasProgramDetailsDataChanges =
      normalizedProgramDetailsNext.host_country !== normalizedProgramDetailsExisting.host_country
      || normalizedProgramDetailsNext.language_requirements !== normalizedProgramDetailsExisting.language_requirements
      || normalizedProgramDetailsNext.submission_type !== normalizedProgramDetailsExisting.submission_type
      || normalizedProgramDetailsNext.has_audiobook_categories !== normalizedProgramDetailsExisting.has_audiobook_categories
      || normalizedProgramDetailsNext.bisac_default_primary_code !== normalizedProgramDetailsExisting.bisac_default_primary_code
      || normalizedProgramDetailsNext.bisac_default_secondary_code !== normalizedProgramDetailsExisting.bisac_default_secondary_code
      || normalizedProgramDetailsNext.bisac_default_tertiary_code !== normalizedProgramDetailsExisting.bisac_default_tertiary_code
      || normalizedProgramDetailsNext.bisac_preferred_edition_year !== normalizedProgramDetailsExisting.bisac_preferred_edition_year
      || normalizedProgramDetailsNext.industry_warnings !== normalizedProgramDetailsExisting.industry_warnings;

    if (!hasMediaChanges && !hasContactChanges && !hasAiInstructionChanges && !hasStep0FlagsChanges && !hasFeesPrizesChanges && !hasRequirementsQcChanges && !hasDeadlinesQcChanges && !hasJudgingQcChanges && !hasProgramDetailsQcChanges && !hasVettingQcChanges && !hasProgramDetailsDataChanges) {
      setHasUnsavedChanges(false);
      return;
    }

    try {
      setIsAutoSaving(true);

      if (hasMediaChanges) {
        const mergedMedia = {
          ...mediaData,
          ...mediaChanges
        };

        await updateMediaMutation.mutateAsync({
          program_id: programId,
          website_url: normalizeString(mergedMedia.website_url ?? null),
          submission_url: normalizeString(mergedMedia.submission_url ?? null),
          past_winners_url: normalizeString(mergedMedia.past_winners_url ?? null),
          logo_url: normalizeString(mergedMedia.logo_url ?? null),
          website_screenshot_url: normalizeString(mergedMedia.website_screenshot_url ?? null),
          seed_urls: normalizeString(mergedMedia.seed_urls ?? null),
          wikipedia_entry: normalizeString(mergedMedia.wikipedia_entry ?? null),
          categories_url: normalizeString(mergedMedia.categories_url ?? null),
          deadlines_url: normalizeString(mergedMedia.deadlines_url ?? null),
          fees_url: normalizeString(mergedMedia.fees_url ?? null),
          requirements_url: normalizeString(mergedMedia.requirements_url ?? null),
          contact_url: normalizeString(mergedMedia.contact_url ?? null),
          guidelines_url: normalizeString(mergedMedia.guidelines_url ?? null),
          qc_flag_media: mergedMedia.qc_flag_media ?? null,
          qc_flag_media_notes: normalizeString(mergedMedia.qc_flag_media_notes ?? null),
          editor_id: null
        });
      }

      if (hasContactChanges) {
        const cleanedContactChanges: Record<string, unknown> = {};
        Object.entries(contactsChanges).forEach(([key, val]) => {
          if (val === '' || val === 'null') {
            cleanedContactChanges[key] = null;
          } else if (val !== null && val !== undefined) {
            cleanedContactChanges[key] = val;
          }
        });

        if (Object.keys(cleanedContactChanges).length > 0) {
          await updateContactsMutation.mutateAsync({
            program_id: programId,
            ...cleanedContactChanges
          });
        }
      }

      if (hasAiInstructionChanges) {
        const normalizedInstructions = normalizeString(programMetaChanges.ai_instructions ?? null);
        const existingInstructions = normalizeString(data.ai_instructions ?? null);

        if (normalizedInstructions !== existingInstructions) {
          await updateAiInstructionsMutation.mutateAsync({
            program_id: programId,
            ai_instructions: normalizedInstructions,
            editor_id: null
          });
        } else {
          setProgramMetaChanges({});
        }
      }

      if (hasStep0FlagsChanges) {
        const currentFlag = programMetaChanges.qc_flag_preparation ?? data.qc_flag_preparation ?? false;
        const currentNotes = normalizeString(programMetaChanges.qc_flag_preparation_notes ?? data.qc_flag_preparation_notes ?? null);
        const existingFlag = data.qc_flag_preparation ?? false;
        const existingNotes = normalizeString(data.qc_flag_preparation_notes ?? null);

        if (currentFlag !== existingFlag || currentNotes !== existingNotes) {
          await updateStep0FlagsMutation.mutateAsync({
            program_id: programId,
            qc_flag_preparation: currentFlag,
            qc_flag_preparation_notes: currentNotes,
            editor_id: null
          });
        }
      }

      if (hasFeesPrizesChanges) {
        const mergedFees = {
          ...feesPrizesData,
          ...feesPrizesChanges
        };

        const normalizeNumber = (value: number | null | undefined): number | null => {
          if (value === null || value === undefined) return null;
          return Number.isFinite(value) ? value : null;
        };

        await updateFeesPrizesMutation.mutateAsync({
          program_id: programId,
          entry_fee: normalizeNumber(mergedFees.entry_fee),
          entry_fee_currency: normalizeString(mergedFees.entry_fee_currency),
          cost_range: normalizeString(mergedFees.cost_range),
          prize_structure: mergedFees.prize_structure ?? null,
          prizes_per_category: normalizeNumber(mergedFees.prizes_per_category),
          prize_types: mergedFees.prize_types ?? null,
          total_win_opportunities: normalizeNumber(mergedFees.total_win_opportunities),
          has_tiered_prizes: mergedFees.has_tiered_prizes ?? null,
          primary_currency: normalizeString(mergedFees.primary_currency),
          extra_benefits: normalizeString(mergedFees.extra_benefits),
          qc_flag_fees_prizes: mergedFees.qc_flag_fees_prizes ?? null,
          qc_flag_fees_prizes_notes: normalizeString(mergedFees.qc_flag_fees_prizes_notes),
          editor_id: null
        });
      }

      if (hasProgramDetailsDataChanges) {
        await updateProgramDetailsDataMutation.mutateAsync({
          program_id: programId,
          host_country: normalizedProgramDetailsNext.host_country,
          language_requirements: normalizedProgramDetailsNext.language_requirements,
          submission_type: normalizedProgramDetailsNext.submission_type,
          has_audiobook_categories: normalizedProgramDetailsNext.has_audiobook_categories,
          bisac_default_primary_code: normalizedProgramDetailsNext.bisac_default_primary_code,
          bisac_default_secondary_code: normalizedProgramDetailsNext.bisac_default_secondary_code,
          bisac_default_tertiary_code: normalizedProgramDetailsNext.bisac_default_tertiary_code,
          bisac_preferred_edition_year: normalizedProgramDetailsNext.bisac_preferred_edition_year,
          industry_warnings: normalizedProgramDetailsNext.industry_warnings,
          editor_id: null
        });
      }

      if (hasRequirementsQcChanges) {
        const currentFlag = requirementsChanges.qc_flag_requirements ?? requirementsData.qc_flag_requirements ?? false;
        const currentNotes = normalizeString(requirementsChanges.qc_flag_requirements_notes ?? requirementsData.qc_flag_requirements_notes ?? null);
        const existingFlag = requirementsData.qc_flag_requirements ?? false;
        const existingNotes = normalizeString(requirementsData.qc_flag_requirements_notes ?? null);

        if (currentFlag !== existingFlag || currentNotes !== existingNotes) {
          await updateRequirementsQcMutation.mutateAsync({
            program_id: programId,
            qc_flag_requirements: currentFlag,
            qc_flag_requirements_notes: currentNotes,
            editor_id: null
          });
        }
      }

      if (hasDeadlinesQcChanges) {
        const currentFlag = deadlinesChanges.qc_flag_deadlines ?? deadlinesData.qc_flag_deadlines ?? false;
        const currentNotes = normalizeString(deadlinesChanges.qc_flag_deadlines_notes ?? deadlinesData.qc_flag_deadlines_notes ?? null);
        const existingFlag = deadlinesData.qc_flag_deadlines ?? false;
        const existingNotes = normalizeString(deadlinesData.qc_flag_deadlines_notes ?? null);

        if (currentFlag !== existingFlag || currentNotes !== existingNotes) {
          await updateDeadlinesQcMutation.mutateAsync({
            program_id: programId,
            qc_flag_deadlines: currentFlag,
            qc_flag_deadlines_notes: currentNotes,
            editor_id: null
          });
        }
      }

      if (hasJudgingQcChanges) {
        const currentFlag = judgingChanges.qc_flag_judging ?? judgingData.qc_flag_judging ?? false;
        const currentNotes = normalizeString(judgingChanges.qc_flag_judging_notes ?? judgingData.qc_flag_judging_notes ?? null);
        const existingFlag = judgingData.qc_flag_judging ?? false;
        const existingNotes = normalizeString(judgingData.qc_flag_judging_notes ?? null);

      if (currentFlag !== existingFlag || currentNotes !== existingNotes) {
        await updateJudgingQcMutation.mutateAsync({
          program_id: programId,
          qc_flag_judging: currentFlag,
          qc_flag_judging_notes: currentNotes,
          editor_id: null
        });
      }
    }

      if (hasProgramDetailsQcChanges) {
        const currentFlag = programDetailsChanges.qc_flag_program_details ?? programDetailsData.qc_flag_program_details ?? false;
        const currentNotes = normalizeString(programDetailsChanges.qc_flag_program_details_notes ?? programDetailsData.qc_flag_program_details_notes ?? null);
        const existingFlag = programDetailsData.qc_flag_program_details ?? false;
        const existingNotes = normalizeString(programDetailsData.qc_flag_program_details_notes ?? null);

        if (currentFlag !== existingFlag || currentNotes !== existingNotes) {
          await updateProgramDetailsQcMutation.mutateAsync({
            program_id: programId,
            qc_flag_program_details: currentFlag,
            qc_flag_program_details_notes: currentNotes,
            editor_id: null
          });
        }
      }

      if (hasVettingQcChanges) {
        const currentFlag = vettingChanges.qc_flag_vetting ?? vettingData.qc_flag_vetting ?? false;
        const currentNotes = normalizeString(vettingChanges.qc_flag_vetting_notes ?? vettingData.qc_flag_vetting_notes ?? null);
        const existingFlag = vettingData.qc_flag_vetting ?? false;
        const existingNotes = normalizeString(vettingData.qc_flag_vetting_notes ?? null);

        if (currentFlag !== existingFlag || currentNotes !== existingNotes) {
          await updateProgramVettingQcMutation.mutateAsync({
            program_id: programId,
            qc_flag_vetting: currentFlag,
            qc_flag_vetting_notes: currentNotes,
            editor_id: null
          });
        }
      }

      markUrlsClean();
      setMediaChanges({});
      setContactsChanges({});
      setRequirementsChanges({});
      setDeadlinesChanges({});
      setJudgingChanges({});
      if (!hasAiInstructionChanges) {
        setProgramMetaChanges({});
      }
      if (!hasFeesPrizesChanges) {
        setFeesPrizesChanges({});
      }
      if (!hasProgramDetailsQcChanges && !hasProgramDetailsDataChanges) {
        setProgramDetailsChanges({});
      }
      if (!hasVettingQcChanges) {
        setVettingChanges({});
      }
      setHasUnsavedChanges(false);
      if (notifySuccess) {
        showMessage('success', '✅ Changes saved successfully', 3000);
      }
    } catch (saveError) {
      console.error('[QC] Save failed', saveError);
      showMessage('error', saveError instanceof Error ? saveError.message : 'Save failed', 4000);
    } finally {
      setIsAutoSaving(false);
      if (autoSaveTimerRef.current !== null) {
        window.clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    }
  };

  const derivedSeedUrl = isNewProgram
    ? newProgramMeta.programUrl.trim()
    : data.seed_urls?.split(',')[0]?.trim() || data.website_url || '';

  const recommendation: URLValidatorRecommendation = validation?.recommendation ?? 'not_run';

  const workflowSteps: QCStepRailStep[] = QC_STEP_DEFINITIONS.map((step) => {
    const completed = checklistState[step.id];
    const firstIncompleteIndex = QC_STEP_DEFINITIONS.findIndex((definition) => !checklistState[definition.id]);
    let status: WorkflowStep['status'] = 'pending';

    if (completed) {
      status = 'complete';
    } else if (firstIncompleteIndex === -1 ? step.id === QC_STEP_DEFINITIONS[0].id : step.id === QC_STEP_DEFINITIONS[firstIncompleteIndex].id) {
      status = 'in_progress';
    }

    const severity = step.id === 'step0'
      ? recommendation === 'AUTO_CONTINUE'
        ? 'ok'
        : recommendation === 'REVIEW_URLS'
          ? 'warning'
          : recommendation === 'INSUFFICIENT_DATA' || recommendation === 'MARK_INELIGIBLE'
            ? 'error'
            : undefined
      : undefined;

    return {
      id: step.id,
      label: step.label,
      status,
      severity
    };
  });

  const activeStepIndex = workflowSteps.findIndex((step) => step.id === activeStepId);
  const activeStep = workflowSteps[activeStepIndex] ?? workflowSteps[0];
  const previousStep = activeStepIndex > 0 ? workflowSteps[activeStepIndex - 1] : undefined;
  const nextStep = activeStepIndex < workflowSteps.length - 1 ? workflowSteps[activeStepIndex + 1] : undefined;

  const handleSelectStep = (stepId: string) => {
    setActiveStepId(stepId);
  };

  const handleNextStep = () => {
    if (nextStep) {
      setActiveStepId(nextStep.id);
    }
  };

  const handlePreviousStep = () => {
    if (previousStep) {
      setActiveStepId(previousStep.id);
    }
  };

  const handleToggleCurrentStepCompletion = () => {
    const isComplete = checklistState[activeStep.id];
    handleChecklistToggle(activeStep.id, !isComplete);
  };

  const handleUrlDiscoveryContinue = () => {
    handleChecklistToggle('step0', true);
    setActiveStepId('step1');
  };

  const handleSendReport = () => {
    alert('Report workflow coming soon. Please compile QA notes and share via the standard channel.');
  };

  const aiInstructionsStep = {
    id: 'step0-ai',
    label: 'Step 0: Preparation (AI Instructions)',
    value: effectiveAiInstructions,
    fieldName: 'programs.ai_instructions' as const,
    flagged: undefined
  };

  const notesSteps = [
    aiInstructionsStep,
    ...QC_STEP_DEFINITIONS.map((step) => {
      let fieldName: string | undefined;
      let flagged: boolean | undefined;
      let value: string = '';

      let label = step.label;

      switch (step.id) {
        case 'step0':
          fieldName = 'programs.qc_flag_preparation_notes';
          flagged = Boolean(programMetaChanges.qc_flag_preparation ?? data.qc_flag_preparation);
          value = notesState[step.id] ?? '';
          label = 'Step 0: Preparation (QC Notes)';
          break;
      case 'step1':
        fieldName = 'media.qc_flag_media_notes';
        flagged = Boolean(mediaChanges.qc_flag_media ?? mediaData.qc_flag_media);
        value = mediaChanges.qc_flag_media_notes ?? mediaData.qc_flag_media_notes ?? '';
        break;
      case 'step2':
        fieldName = 'requirements.qc_flag_requirements_notes';
        flagged = Boolean(requirementsChanges.qc_flag_requirements ?? requirementsData.qc_flag_requirements);
        value = requirementsChanges.qc_flag_requirements_notes ?? requirementsData.qc_flag_requirements_notes ?? '';
        break;
      case 'step3':
        fieldName = 'deadlines.qc_flag_deadlines_notes';
        flagged = Boolean(deadlinesChanges.qc_flag_deadlines ?? deadlinesData.qc_flag_deadlines);
        value = deadlinesChanges.qc_flag_deadlines_notes ?? deadlinesData.qc_flag_deadlines_notes ?? '';
        break;
      case 'step4':
        fieldName = 'fees_prizes.qc_flag_fees_prizes_notes';
        flagged = Boolean(feesPrizesChanges.qc_flag_fees_prizes ?? feesPrizesData.qc_flag_fees_prizes);
        value = feesPrizesChanges.qc_flag_fees_prizes_notes ?? feesPrizesData.qc_flag_fees_prizes_notes ?? '';
        break;
      case 'step5':
        fieldName = 'categories.qc_flag_categories_notes';
        flagged = Boolean(categoriesData.qc_flag_categories);
        value = categoriesData.qc_flag_categories_notes ?? '';
        break;
      case 'step6':
        fieldName = 'contacts.qc_flag_contacts_notes';
        flagged = Boolean(contactsChanges.qc_flag_contacts ?? contactsData.qc_flag_contacts);
        value = contactsChanges.qc_flag_contacts_notes ?? contactsData.qc_flag_contacts_notes ?? '';
        break;
      case 'step7':
        fieldName = 'judging.qc_flag_judging_notes';
        flagged = Boolean(judgingChanges.qc_flag_judging ?? judgingData.qc_flag_judging);
        value = judgingChanges.qc_flag_judging_notes ?? judgingData.qc_flag_judging_notes ?? '';
        break;
      case 'step8':
        fieldName = 'program_details.qc_flag_program_details_notes';
        flagged = Boolean(programDetailsChanges.qc_flag_program_details ?? programDetailsData.qc_flag_program_details);
        value = programDetailsChanges.qc_flag_program_details_notes ?? programDetailsData.qc_flag_program_details_notes ?? '';
        break;
      case 'step9':
        fieldName = 'vetting.qc_flag_vetting_notes';
        flagged = Boolean(vettingChanges.qc_flag_vetting ?? vettingData.qc_flag_vetting);
        value = vettingChanges.qc_flag_vetting_notes ?? vettingData.qc_flag_vetting_notes ?? '';
        break;
      default:
        value = notesState[step.id] ?? '';
        break;
    }

      return {
        id: step.id,
        label,
        value,
        fieldName,
        flagged
      };
    })
  ];

  const isCurrentStepComplete = Boolean(checklistState[activeStep.id]);

  let headerFlagged: boolean | null = null;
  let headerOnToggleFlag: (() => void) | undefined;
  const headerFlagEditable = editMode && !isNewProgram;

  const stepContent = (() => {
    switch (activeStep.id) {
      case 'step0':
        if (isNewProgram) {
          const canContinue = newProgramMeta.programUrl.trim() !== '' && newProgramMeta.validationQuestion.trim() !== '';

          return (
            <NewProgramPreparationStep
              programUrl={newProgramMeta.programUrl}
              validationQuestion={newProgramMeta.validationQuestion}
              canContinue={canContinue}
              onProgramUrlChange={(value) => {
                setNewProgramMeta((prev) => ({
                  ...prev,
                  programUrl: value
                }));
                setHasUnsavedChanges(true);
              }}
              onValidationQuestionChange={(value) => {
                setNewProgramMeta((prev) => ({
                  ...prev,
                  validationQuestion: value
                }));
                setHasUnsavedChanges(true);
              }}
              onContinue={() => {
                if (!canContinue) {
                  return;
                }
                handleChecklistToggle('step0', true);
                setActiveStepId('step1');
              }}
              stepCompleted={checklistState.step0}
            />
          );
        }

        headerFlagged = Boolean(programMetaChanges.qc_flag_preparation ?? data.qc_flag_preparation);
        headerOnToggleFlag = () => {
          const next = !(programMetaChanges.qc_flag_preparation ?? data.qc_flag_preparation ?? false);
          setProgramMetaChanges((prev) => ({
            ...prev,
            qc_flag_preparation: next,
            qc_flag_preparation_notes: prev.qc_flag_preparation_notes ?? data.qc_flag_preparation_notes ?? null
          }));
          markDirty();
        };

        return (
          <TableSection
            title="Step 0: Preparation"
            description="Review the starting context before diving into deeper checks."
            tableName="media"
            programId={programId}
            baciCode={baciCode}
            seedUrl={derivedSeedUrl}
            organization={data.awarding_organization || undefined}
            editMode={editMode}
          >
            <div className={styles.sectionStack}>
              <FieldRow
                label="Program URL"
                value={mediaChanges.website_url ?? mediaData.website_url}
                editable={editMode}
                onChange={(value) => {
                  setMediaChanges((prev) => ({
                    ...prev,
                    website_url: value
                  }));
                  markDirty();
                }}
              />
              <FieldRow
                label="Category URLs"
                value={mediaChanges.categories_url ?? mediaData.categories_url}
                editable={editMode}
                onChange={(value) => {
                  setMediaChanges((prev) => ({
                    ...prev,
                    categories_url: value
                  }));
                  markDirty();
                }}
                multiline
                placeholder="Paste category landing URLs…"
              />
            </div>
            <UrlDiscoveryStep
              programId={programId}
              baciCode={baciCode}
              onComplete={handleUrlDiscoveryContinue}
              onStepComplete={(completed) => handleChecklistToggle('step0', completed)}
              onRegisterRunner={handleRegisterDiscoveryRunner}
              onLoadingChange={handleDiscoveryLoadingChange}
            />
          </TableSection>
        );
      case 'step1':
        headerFlagged = Boolean(mediaChanges.qc_flag_media ?? mediaData.qc_flag_media);
        headerOnToggleFlag = () => {
          const next = !(mediaChanges.qc_flag_media ?? mediaData.qc_flag_media ?? false);
          setMediaChanges((prev) => ({
            ...prev,
            qc_flag_media: next,
            qc_flag_media_notes: prev.qc_flag_media_notes ?? mediaData.qc_flag_media_notes ?? null
          }));
          markDirty();
        };

        return (
          <TableSection
            title="Step 1: Validate Seed URLs"
            description="Confirm every source URL before moving to requirements, deadlines, fees, and categories."
            tableName="media"
            programId={programId}
            baciCode={baciCode}
            seedUrl={derivedSeedUrl}
            organization={data.awarding_organization || undefined}
            editMode={editMode}
          >
            <PreparationSection
              data={{
                ...mediaData,
                ...mediaChanges
              }}
              editable={editMode}
              validation={validation ?? null}
              validating={validating}
              urlsDirty={urlsDirty}
              onChange={(updates) => {
                setMediaChanges(updates);
                markDirty();
              }}
              onSaveOverrides={(overrides) => {
                if (!validation) return;

                const updatedValidation: typeof validation = {
                  ...validation,
                  data_availability: {
                    ...validation.data_availability,
                    ...overrides
                  }
                };

                queryClient.setQueryData(['url-validation', programId], updatedValidation);
                showMessage('success', 'Overrides saved to session', 2500);
                markDirty();
              }}
            />
          </TableSection>
        );
      case 'step2':
        headerFlagged = Boolean(requirementsChanges.qc_flag_requirements ?? requirementsData.qc_flag_requirements);
        headerOnToggleFlag = () => {
          const next = !(requirementsChanges.qc_flag_requirements ?? requirementsData.qc_flag_requirements ?? false);
          setRequirementsChanges((prev) => ({
            ...prev,
            qc_flag_requirements: next,
            qc_flag_requirements_notes: prev.qc_flag_requirements_notes ?? requirementsData.qc_flag_requirements_notes ?? null
          }));
          markDirty();
        };

        return (
          <TableSection
            title="Step 2: Requirements"
            description="Confirm program eligibility requirements and capture any missing criteria."
            tableName="requirements"
            programId={programId}
            baciCode={baciCode}
            seedUrl={derivedSeedUrl}
            organization={data.awarding_organization || undefined}
            editMode={editMode}
            runLabel="Run AI research"
            runDisabled={!editMode}
            onRegisterRun={handleRegisterRun}
          >
            <RequirementsSection
              data={{
                ...requirementsData,
                ...requirementsChanges
              }}
              editable={editMode}
              onChange={(updates) => {
                setRequirementsChanges(updates);
                markDirty();
              }}
            />
          </TableSection>
        );
      case 'step3':
        headerFlagged = Boolean(deadlinesChanges.qc_flag_deadlines ?? deadlinesData.qc_flag_deadlines);
        headerOnToggleFlag = () => {
          const next = !(deadlinesChanges.qc_flag_deadlines ?? deadlinesData.qc_flag_deadlines ?? false);
          setDeadlinesChanges((prev) => ({
            ...prev,
            qc_flag_deadlines: next,
            qc_flag_deadlines_notes: prev.qc_flag_deadlines_notes ?? deadlinesData.qc_flag_deadlines_notes ?? null
          }));
          markDirty();
        };

        return (
          <TableSection
            title="Step 3: Deadlines"
            description="Ensure deadline structure and milestone dates match the official source."
            tableName="deadlines"
            programId={programId}
            baciCode={baciCode}
            seedUrl={derivedSeedUrl}
            organization={data.awarding_organization || undefined}
            editMode={editMode}
            runLabel="Run AI research"
            runDisabled={!editMode}
            onRegisterRun={handleRegisterRun}
          >
            <DeadlinesSection
              data={{
                ...deadlinesData,
                ...deadlinesChanges
              }}
              editable={editMode}
              onChange={(updates) => {
                setDeadlinesChanges(updates);
                markDirty();
              }}
            />
          </TableSection>
        );
      case 'step4':
        headerFlagged = Boolean(feesPrizesChanges.qc_flag_fees_prizes ?? feesPrizesData.qc_flag_fees_prizes);
        headerOnToggleFlag = () => {
          const next = !(feesPrizesChanges.qc_flag_fees_prizes ?? feesPrizesData.qc_flag_fees_prizes ?? false);
          setFeesPrizesChanges((prev) => ({
            ...prev,
            qc_flag_fees_prizes: next,
            qc_flag_fees_prizes_notes: prev.qc_flag_fees_prizes_notes ?? feesPrizesData.qc_flag_fees_prizes_notes ?? null
          }));
          markDirty();
        };

        return (
          <TableSection
            title="Step 4: Fees & Prizes"
            description="Review fee schedule and prize structure so pricing guidance stays accurate."
            tableName="fees_prizes"
            programId={programId}
            baciCode={baciCode}
            seedUrl={derivedSeedUrl}
            organization={data.awarding_organization || undefined}
            editMode={editMode}
            runLabel="Run AI research"
            runDisabled={!editMode}
            onRegisterRun={handleRegisterRun}
          >
            <FeesPrizesSection
              data={{
                ...feesPrizesData,
                ...feesPrizesChanges
              }}
              editable={editMode}
              onChange={(updates) => {
                setFeesPrizesChanges(updates);
                markDirty();
              }}
            />
          </TableSection>
        );
      case 'step5':
        return (
          <TableSection
            title="Step 5: Categories"
            description="Validate the categories list and flag inconsistencies for taxonomy alignment."
            tableName="categories"
            programId={programId}
            baciCode={baciCode}
            seedUrl={derivedSeedUrl}
            organization={data.awarding_organization || undefined}
            editMode={editMode}
            runLabel="Run AI research"
            runDisabled={!editMode}
            onRegisterRun={handleRegisterRun}
          >
            <CategoriesSection
              data={categoriesData}
              editable={editMode}
              onChange={() => {
                markDirty();
              }}
            />
          </TableSection>
        );
      case 'step6':
        headerFlagged = Boolean(contactsChanges.qc_flag_contacts ?? contactsData.qc_flag_contacts);
        headerOnToggleFlag = () => {
          const next = !(contactsChanges.qc_flag_contacts ?? contactsData.qc_flag_contacts ?? false);
          setContactsChanges((prev) => ({
            ...prev,
            qc_flag_contacts: next,
            qc_flag_contacts_notes: prev.qc_flag_contacts_notes ?? contactsData.qc_flag_contacts_notes ?? null
          }));
          markDirty();
        };

        return (
          <TableSection
            title="Step 6: Contacts"
            description="Keep contacts current so outreach and follow-up stay reliable."
            tableName="contacts"
            programId={programId}
            baciCode={baciCode}
            seedUrl={derivedSeedUrl}
            organization={data.awarding_organization || undefined}
            editMode={editMode}
            runLabel="Run AI research"
            runDisabled={!editMode}
            onRegisterRun={handleRegisterRun}
            onApplySuggestions={(acceptedChanges) => {
              setContactsChanges((prev) => ({
                ...prev,
                ...acceptedChanges
              }));
              markDirty();
            }}
          >
            <ContactsSection
              data={{
                ...contactsData,
                ...contactsChanges
              }}
              editable={editMode}
              onChange={(updates) => {
                setContactsChanges(updates);
                markDirty();
              }}
            />
          </TableSection>
        );
      case 'step7':
        headerFlagged = Boolean(judgingChanges.qc_flag_judging ?? judgingData.qc_flag_judging);
        headerOnToggleFlag = () => {
          const next = !(judgingChanges.qc_flag_judging ?? judgingData.qc_flag_judging ?? false);
          setJudgingChanges((prev) => ({
            ...prev,
            qc_flag_judging: next,
            qc_flag_judging_notes: prev.qc_flag_judging_notes ?? judgingData.qc_flag_judging_notes ?? null
          }));
          markDirty();
        };

        return (
          <TableSection
            title="Step 7: Judging"
            description="Confirm judging criteria and celebration details to finish the narrative."
            tableName="judging"
            programId={programId}
            baciCode={baciCode}
            seedUrl={derivedSeedUrl}
            organization={data.awarding_organization || undefined}
            editMode={editMode}
            runLabel="Run AI research"
            runDisabled={!editMode}
            onRegisterRun={handleRegisterRun}
          >
            <JudgingSection
              data={{
                ...judgingData,
                ...judgingChanges
              }}
              editable={editMode}
              onChange={(updates) => {
                setJudgingChanges(updates);
                markDirty();
              }}
            />
          </TableSection>
        );
      case 'step8':
        headerFlagged = Boolean(programDetailsChanges.qc_flag_program_details ?? programDetailsData.qc_flag_program_details);
        headerOnToggleFlag = () => {
          const next = !(programDetailsChanges.qc_flag_program_details ?? programDetailsData.qc_flag_program_details ?? false);
          setProgramDetailsChanges((prev) => ({
            ...prev,
            qc_flag_program_details: next,
            qc_flag_program_details_notes: prev.qc_flag_program_details_notes ?? programDetailsData.qc_flag_program_details_notes ?? null
          }));
          markDirty();
        };

        return (
          <TableSection
            title="Step 8: Program Details"
            description="Confirm host country, BISAC defaults, and other program-level metadata are accurate."
            tableName="program_details"
            programId={programId}
            baciCode={baciCode}
            seedUrl={derivedSeedUrl}
            organization={data.awarding_organization || undefined}
            editMode={editMode}
          >
            <ProgramDetailsSection
              data={{
                ...programDetailsData,
                ...programDetailsChanges
              }}
              editable={editMode}
              onChange={(updates) => {
                setProgramDetailsChanges((prev) => ({
                  ...prev,
                  ...updates
                }));
                markDirty();
              }}
            />
          </TableSection>
        );
      case 'step9':
        return (
          <TableSection
            title="Step 9: Vetting"
            description="Review vetting history, confirm status accuracy, and capture any follow-up actions."
            tableName="vetting"
            programId={programId}
            baciCode={baciCode}
            seedUrl={derivedSeedUrl}
            organization={data.awarding_organization || undefined}
            editMode={editMode}
          >
          <VettingSection
            data={{
              ...vettingData,
              ...vettingChanges
            }}
            editable={editMode}
            onChange={(updates) => {
              setVettingChanges((prev) => ({
                ...prev,
                ...updates
              }));
              markDirty();
            }}
            onStatusChange={editMode ? (next) => {
              const mapped = mapBackendVettingToUi(next);
              if (mapped === vettingStatus) {
                return true;
              }
              return handleVettingStatusChange(mapped);
            } : undefined}
            statusSaving={vettingSaving}
            currentStatus={mapUiVettingToBackend(vettingStatus)}
          />
        </TableSection>
        );
      case 'step10':
        return (
          <div className={styles.placeholderCard}>
            <h3>{activeStep.label}</h3>
            <p>We are finalizing this workflow step. Document your findings in notes for now.</p>
          </div>
        );
      case 'step11':
        return (
          <div className={styles.finalizeCard}>
            <h3>Finalize Session</h3>
            <p>Review notes, confirm checklist completion, and share the QA summary with the reporting channel.</p>
            <div className={styles.finalizeActions}>
              <button type="button" className={styles.finalizeButton} onClick={handleSendReport}>
                Send QA Report
              </button>
              <button
                type="button"
                className={styles.secondaryFinalizeButton}
                onClick={() => {
                  setNotesOpen(true);
                }}
              >
                Review Notes
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  })();

  let headerRunConfig = registeredRunConfig;

if (activeStep.id === 'step0') {
  headerRunConfig = {
    label: 'Get Seed URLs',
    run: async () => {
      if (urlDiscoveryRunnerRef.current) {
        await urlDiscoveryRunnerRef.current();
      }
    },
    disabled: isNewProgram || isDiscoveryRunning || !hasDiscoveryRunner || !editMode
  };
} else if (activeStep.id === 'step1') {
  headerRunConfig = {
    label: 'Run URL validation',
    run: async () => {
      await handleRunValidation();
    },
    disabled: isNewProgram || validating || !editMode
  };
}

const primaryActionLabel = headerRunConfig?.label ?? null;
const primaryActionDisabled = (headerRunConfig?.disabled ?? false) || vettingSaving;
const primaryActionHandler = headerRunConfig
    ? () => {
        const maybePromise = headerRunConfig.run();
        if (maybePromise && typeof (maybePromise as Promise<void>).then === 'function') {
          void (maybePromise as Promise<void>);
        }
      }
    : undefined;

  return (
    <div className={`${styles.container} ${className}`}>
      <QCHeader hasUnsavedChanges={hasUnsavedChanges} />

      <QCStepHeader
        programName={displayProgramName}
        programCode={displayProgramCode}
        websiteUrl={displayWebsiteUrl}
        currentIndex={!isNewProgram ? effectiveNavData?.currentIndex : undefined}
        totalPrograms={!isNewProgram ? effectiveNavData?.total : undefined}
        hasPreviousProgram={previousButtonEnabled}
        hasNextProgram={nextButtonEnabled}
        onPreviousProgram={previousButtonEnabled ? handlePreviousProgramClick : undefined}
        onNextProgram={nextButtonEnabled ? handleNextProgramClick : undefined}
        isLocked={isNewProgram ? false : displayLocked}
        onToggleLock={isNewProgram ? () => {} : handleToggleLock}
        lockPending={isNewProgram || isLockMutationPending || lockStatusLoading || pendingLockAction !== null}
        timerLabel={timerLabel}
        timerRunning={timerState.running}
        onToggleTimer={handleToggleTimer}
        onResetTimer={handleResetTimer}
        hasUnsavedChanges={hasUnsavedChanges}
        onOpenNotes={() => setNotesOpen(true)}
        stepCompleted={isCurrentStepComplete}
        onToggleStepComplete={handleToggleCurrentStepCompletion}
        canToggleStepComplete={!isNewProgram}
        flagged={typeof headerFlagged === 'boolean' ? headerFlagged : undefined}
        onToggleFlag={headerOnToggleFlag}
        flagEditable={headerFlagEditable}
        primaryActionLabel={primaryActionLabel}
        primaryActionDisabled={primaryActionDisabled}
        onPrimaryAction={primaryActionHandler}
        vettingStatus={vettingStatus}
        onVettingStatusChange={!vettingSaving && !isNewProgram && editMode ? handleVettingStatusChange : undefined}
      />

      <div className={styles.layout}>
        <QCStepRail steps={workflowSteps} activeStepId={activeStep.id} onSelectStep={handleSelectStep} />

        <div className={styles.contentColumn}>
          {stepContent}

          <QCStepFooter
            currentStepLabel={activeStep.label}
            previousStepLabel={previousStep?.label}
            nextStepLabel={nextStep?.label}
            onPreviousStep={handlePreviousStep}
            onNextStep={handleNextStep}
            canGoPrevious={Boolean(previousStep)}
            canGoNext={Boolean(nextStep)}
          />
        </div>
      </div>

      <QCNotesDrawer
        open={notesOpen}
        steps={notesSteps}
        onChange={handleNoteChange}
        onClose={() => setNotesOpen(false)}
      />

      {!lockStatusLoading && !effectiveLockState && hasUnsavedChanges && (
        <div className={styles.saveBar}>
          <span className={styles.warningIcon}>⚠️</span>
          <span>
            {isNewProgram
              ? 'Unsaved changes are stored locally for this new program session.'
              : isAutoSaving
                ? 'Saving changes…'
                : 'Unsaved changes will auto-save shortly.'}
          </span>
          <div className={styles.saveActions}>
            <button
              type="button"
              onClick={() => {
                if (autoSaveTimerRef.current !== null) {
                  window.clearTimeout(autoSaveTimerRef.current);
                  autoSaveTimerRef.current = null;
                }
                setHasUnsavedChanges(false);
                setContactsChanges({});
                setMediaChanges({});
                setRequirementsChanges({});
                setDeadlinesChanges({});
                setFeesPrizesChanges({});
                setJudgingChanges({});
                setProgramMetaChanges({});
                setProgramDetailsChanges({});
                setVettingChanges({});
                if (!isNewProgram) {
                  void refetch();
                }
              }}
              className={styles.cancelButton}
              disabled={isAutoSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                if (isNewProgram) {
                  return;
                }
                void persistPendingChanges(true);
              }}
              className={styles.saveButton}
              disabled={
                isNewProgram
                || isAutoSaving
                || updateContactsMutation.isPending
                || updateMediaMutation.isPending
                || updateAiInstructionsMutation.isPending
                || updateFeesPrizesMutation.isPending
                || updateRequirementsQcMutation.isPending
                || updateDeadlinesQcMutation.isPending
                || updateJudgingQcMutation.isPending
                || updateProgramDetailsQcMutation.isPending
                || updateProgramVettingQcMutation.isPending
                || updateProgramDetailsDataMutation.isPending
              }
            >
              {isNewProgram
                ? 'Save unavailable'
                : (isAutoSaving
                  || updateContactsMutation.isPending
                  || updateMediaMutation.isPending
                  || updateAiInstructionsMutation.isPending
                  || updateFeesPrizesMutation.isPending
                  || updateRequirementsQcMutation.isPending
                  || updateDeadlinesQcMutation.isPending
                  || updateJudgingQcMutation.isPending
                  || updateProgramDetailsQcMutation.isPending
                  || updateProgramVettingQcMutation.isPending
                  || updateProgramDetailsDataMutation.isPending)
                  ? 'Saving…'
                  : 'Save now'}
            </button>
          </div>
        </div>
      )}

      {saveMessage && (
        <div
          className={`${styles.saveMessage} ${saveMessage.type === 'success' ? styles.saveMessageSuccess : styles.saveMessageError}`}
        >
          <span className={styles.messageIcon}>
            {saveMessage.type === 'success' ? '✓' : '✗'}
          </span>
          <span>{saveMessage.text}</span>
        </div>
      )}
    </div>
  );
}

export default QualityCheckCard;
